/** Session flags for interview-guide UX until job API exposes context-pack status on the DTO. */

/** Indicates whether the Step 1 context pack was built from all sources ('full') or a subset ('partial'). */
export type InterviewContextPackStatus = 'full' | 'partial';

/** Ordered SSE phase labels shown in the generating timeline UI. */
export const GENERATION_PHASE_STEPS = [
  { phase: 'assessment.context', label: 'Analyzing your engagement context…' },
  { phase: 'assessment.scatter', label: 'Generating question frameworks…' },
  { phase: 'assessment.merge', label: 'Aligning frameworks to stakeholder roles…' },
  { phase: 'assessment.synthesize', label: 'Assembling your interview guide…' },
  { phase: 'assessment.validating', label: 'Reviewing guide quality…' },
] as const;

/** Union of valid SSE phase identifiers emitted during Step 1 generation. */
export type GenerationPhase = (typeof GENERATION_PHASE_STEPS)[number]['phase'];

/**
 * Return the 0-based index of a phase in GENERATION_PHASE_STEPS, or -1 when not found.
 *
 * @param phase - The SSE phase string to look up, or null to get -1.
 */
export const phaseToStepIndex = (phase: GenerationPhase | null): number => {
  if (!phase) {
    return -1;
  }
  return GENERATION_PHASE_STEPS.findIndex((s) => s.phase === phase);
};

/** Return the sessionStorage key used to persist the context-pack status for one assessment. */
export const interviewGuideContextPackStorageKey = (assessmentInstanceId: string): string =>
  `bdp.interviewGuide.${assessmentInstanceId}.contextPackStatus`;

const interviewGuideMarkdownSessionStorageKey = (assessmentInstanceId: string): string =>
  `bdp.interviewGuide.${assessmentInstanceId}.markdownPreview`;

/**
 * Stand-in for API-returned Markdown after step 1 generation completes (until `interview_guide_markdown` is persisted server-side).
 * Kept in one place so Step 2 can show the same shape users expect from the skill contract.
 */
export const MOCK_GENERATED_INTERVIEW_GUIDE_MARKDOWN = `# Interview guides — Sample engagement

- **Priorities:** Data modernization, governance, stakeholder alignment.
- **Constraints:** Legacy platforms, delivery timeline pressure.
- **Format:** 60-minute virtual stakeholder interviews.

-------

# Role-Specific Question Guides

## Chief Data Officer — Alex Rivera
Focus: enterprise data strategy, quality, and AI readiness.

1. How do you prioritize data quality versus speed of delivery today, and who owns the trade-offs?
2. What would a successful outcome from this assessment look like for you in the next 90 days?
3. Where do current reporting and lineage practices break down first when something goes wrong?

## Director of IT Operations — Jordan Lee
Focus: migration risks, run-state handoffs, and operational resilience.

1. Walk through your last major platform change—what failed first, and how did the team recover?
2. Which handoffs between teams create the most incident drag today?
3. What operational metrics do you trust least, and why?

## VP, Clinical Strategy — Morgan Chen
Focus: care delivery priorities, adoption of new workflows, and change leadership.

1. Where do clinicians feel the biggest friction between policy intent and day-to-day practice?
2. How do you decide what to pilot versus scale, and who holds the pen on go/no-go?
3. What would convince you this assessment produced something actionable—not another shelf report?

## Director of Finance & Performance — Riley Patel
Focus: ROI, funding constraints, and measurable outcomes.

1. Which investments in data or analytics produced the clearest return—and which did not, and why?
2. How are benefits cases written today, and what evidence standard do you need to approve spend?
3. Where does cost transparency break down across service lines or business units?

# Probe Bank — Common Responses

- **Clarify scope** — Use when answers stay at the strategy layer without concrete examples.
- **Quantify impact** — Use when pain is described but not measurable.
- **Name the owner** — Use when accountability for a decision or system is ambiguous.

## Red Flags to Watch For

- Inconsistent ownership or timelines across leaders for the same dependency.
- “We’ll fix it in the migration” as a recurring answer without mitigation detail.
`;

/**
 * Read the context-pack status from sessionStorage.
 *
 * @param assessmentInstanceId - UUID of the assessment instance.
 * @returns 'full' | 'partial', or null when not set or storage is unavailable.
 */
export const readInterviewContextPackStatus = (
  assessmentInstanceId: string,
): InterviewContextPackStatus | null => {
  try {
    const raw = sessionStorage.getItem(interviewGuideContextPackStorageKey(assessmentInstanceId));
    if (raw === 'partial' || raw === 'full') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
};

/**
 * Read the transiently stored interview guide Markdown from sessionStorage.
 *
 * Used to show the generated guide on Step 2 before the API persists it server-side.
 *
 * @param assessmentInstanceId - UUID of the assessment instance.
 * @returns The stored Markdown string, or null when absent or storage is unavailable.
 */
export const readInterviewGuideMarkdownSession = (assessmentInstanceId: string): string | null => {
  try {
    const raw = sessionStorage.getItem(
      interviewGuideMarkdownSessionStorageKey(assessmentInstanceId),
    );
    if (raw && raw.trim().length > 0) {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
};

/**
 * Persist generated interview guide Markdown to sessionStorage until the server confirms persistence.
 *
 * @param assessmentInstanceId - UUID of the assessment instance.
 * @param markdown - The raw Markdown string returned by the LLM job.
 */
export const writeInterviewGuideMarkdownSession = (
  assessmentInstanceId: string,
  markdown: string,
): void => {
  try {
    sessionStorage.setItem(interviewGuideMarkdownSessionStorageKey(assessmentInstanceId), markdown);
  } catch {
    // ignore quota / private mode
  }
};

const guideSelectionStorageKey = (assessmentInstanceId: string): string =>
  `bdp.interviewPrep.guideSelection.${assessmentInstanceId}`;

/**
 * Persist the skill selector choice to sessionStorage for rehydration after navigation.
 *
 * The value format is `template:<key>` or `version:<uuid>` and is stored as a minimal JSON
 * object that maps directly to the InterviewGuideGenerateRequest payload fields.
 *
 * @param assessmentInstanceId - UUID of the assessment instance.
 * @param selectedValue - Prefixed selector value from the skill dropdown.
 */
export const writeGuideSelection = (assessmentInstanceId: string, selectedValue: string): void => {
  try {
    if (selectedValue.startsWith('template:')) {
      sessionStorage.setItem(
        guideSelectionStorageKey(assessmentInstanceId),
        JSON.stringify({ template_key: selectedValue.slice('template:'.length) }),
      );
    } else if (selectedValue.startsWith('version:')) {
      sessionStorage.setItem(
        guideSelectionStorageKey(assessmentInstanceId),
        JSON.stringify({ skill_version_id: selectedValue.slice('version:'.length) }),
      );
    }
  } catch {
    // ignore quota / private mode
  }
};

/**
 * Trigger a browser file download of Markdown content without a server round-trip.
 *
 * Creates an object URL, clicks an invisible anchor, then revokes the URL.
 *
 * @param markdown - The Markdown string to write into the downloaded file.
 * @param filename - The suggested filename shown in the browser's save dialog.
 */
export const downloadMarkdownFile = (markdown: string, filename: string): void => {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
